import { Offcanvas, Form, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import HelperService from "../../utility/HelperService";

interface MediaFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  name: string;
  description: string;
  publishedAt: string;
  location: string;
  categoryId: string;
  slug: string;
};
interface Tag {
  _id: string;
  name: string;
}
const AddGallery = ({ show, handleClose, data }: MediaFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [tagsList, setTagsList] = useState<Tag[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const nameValue = watch("name");
  useEffect(() => {

    //Edit mode me slug ko kabhi change nahi karna
    if (data?._id) return;
    
    if (!nameValue) {
      setValue("slug", "");
      return;
    }

    // ✅ Only auto-generate in Add mode
    if (!data?._id) {
      const slug = HelperService.generateSlug(nameValue);
      setValue("slug", slug);
    }
  }, [nameValue, data]);  
  // ✅ Load existing data if editing
  useEffect(() => {
    if (show) {
      fetchTags().then(() => {
        reset({
          name: data?.name || "",
          slug: data?.slug || "",
          publishedAt: data?.publishedAt
            ? new Date(data.publishedAt).toISOString().slice(0, 10)
            : "",
          location: data?.location || "",
          categoryId:
            typeof data?.categoryId === "object"
              ? data?.categoryId?._id
              : data?.categoryId || "",
        });
      });
      setExistingImages(data?.images || []); // ✅ store existing images
      setFiles([]);
    } else {
      reset({});
      setFiles([]);
      setExistingImages([]);
    }
  }, [show, data, reset]);
  const handleRemoveExistingImage = (index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };
  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `categories/get-list/GALLERY`,
        body: null,
      });

      const tags: Tag[] = res?.data || [];
      setTagsList(tags);

      // ✅ If editing existing data, auto-select the tag
      if (data?.categoryId) {
        const tagExists = tags.some((tag: Tag) => tag._id === data.categoryId);
        if (tagExists) setValue("categoryId", data.categoryId);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };
  const ALLOWED_IMAGE_TYPES = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
  ];

  // ✅ Handle multiple file uploads
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const inputFiles = input.files ? Array.from(input.files) : [];

    if (!inputFiles.length) return;

    // ❌ max 10 files
    if (files.length + inputFiles.length > 10) {
      toast.warning("You can upload a maximum of 10 files.");
      input.value = "";
      return;
    }

    // ❌ filter invalid images
    const invalidFiles = inputFiles.filter(
      (file) => !ALLOWED_IMAGE_TYPES.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      toast.error(
        "Image format not supported. Please upload PNG, JPG, JPEG or SVG only."
      );
    }

    // ✅ keep only valid images
    const validFiles = inputFiles.filter((file) =>
      ALLOWED_IMAGE_TYPES.includes(file.type)
    );

    if (!validFiles.length) {
      input.value = "";
      return;
    }

    // ✅ append valid files
    setFiles((prev) => [...prev, ...validFiles]);

    // reset input so same file can be selected again
    input.value = "";
  };

  // ✅ Remove a selected file before upload
  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Submit form
  const onSubmit = (request: FormData) => {
    const totalImages = existingImages.length + files.length;
    if (totalImages === 0) {
      toast.error("At least 1 image is required");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("name", request.name);
    formData.append("slug", request.slug || "");
    formData.append("location", request.location || "");
    if (request.publishedAt) {
      const date = new Date(request.publishedAt);
      formData.append("publishedAt", date.toISOString());
    }
    if (request.categoryId) formData.append("categoryId", request.categoryId);

    // ✅ Send updated image list JSON
    formData.append("existingImages", JSON.stringify(existingImages));

    // ✅ Add newly selected files
    files.forEach((file) => {
      formData.append("images", file);
    });

    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
          action: `gallery/edit/${data._id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `gallery/add`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(res.message || "Gallery updated successfully!");
        reset();
        setFiles([]);
        handleClose();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Upload failed");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setFiles([]);
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {data ? "Edit Gallery" : "Add Gallery"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>

            {/* Title */}
            <Form.Group className="mb-3" controlId="mediaTitle">
              <Form.Label className="label">
                Gallery Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <div className="error-message">{errors.name.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="slugField">
              <Form.Label className="label">
                Slug <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Auto-generated slug"
                {...register("slug", { required: "Slug is required" })}
                // disabled={!data?._id}
                // readOnly={!data?._id}
              />
              {errors.slug && (
                <div className="error-message">{errors.slug.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="mediaLocation">
              <Form.Label className="label">
                Location <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter location"
                {...register("location", { required: "Location is required" })}
              />
              {errors.location && (
                <div className="error-message">{errors.location.message}</div>
              )}
            </Form.Group>
            {/* Tags Dropdown */}
            <Form.Group className="mb-2" controlId="eventTag">
              <Form.Label className="label">
                Category <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("categoryId", { required: "Please select a tag" })}
                size="sm"
              >
                <option value="">Select Category</option>
                {tagsList.map((tag: { _id: string; name: string }) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Select>

              {errors.categoryId && (
                <div className="error-message">{errors.categoryId.message}</div>
              )}
            </Form.Group>
            {/* Date */}
            <Form.Group className="mb-3" controlId="mediaDate">
              <Form.Label className="label">Event Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                {...register("publishedAt", {
                  required: "Date is required",
                })}
              />
              {errors.publishedAt && (
                <div className="error-message">
                  {errors.publishedAt.message}
                </div>
              )}
            </Form.Group>
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Media
            </span>
            {/* Upload Multiple Files */}
            {/* Upload Multiple Images */}
            <Form.Group className="mb-3" controlId="mediaFiles">
              <Form.Label className="label">Upload Images (Max 10) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />
              <span className="text-primary" style={{ fontSize: "12px" }}>
                Note: Please upload an image of size 550×490 in .png, .jpeg,
                .jpg or .svg format{" "}
              </span>
              {/* Existing images from the API */}
              {existingImages.length > 0 && (
                <div className="mt-3">
                  <Form.Label className="fw-bold">Existing Images</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {existingImages.map((imgUrl: string, index: number) => (
                      <div
                        key={index}
                        className="position-relative"
                        style={{ display: "inline-block" }}
                      >
                        <img
                          src={imgUrl}
                          alt={`existing-${index}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                          }}
                        />
                        {/* ❌ Remove existing image */}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          onClick={() => handleRemoveExistingImage(index)}
                          style={{
                            transform: "translate(30%, -30%)",
                            borderRadius: "50%",
                            padding: "0px 6px",
                          }}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New image previews before upload */}
              {files.length > 0 && (
                <div className="mt-3">
                  <Form.Label className="fw-bold">Selected Images</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="position-relative"
                        style={{ display: "inline-block" }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${index}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          onClick={() => handleRemoveFile(index)}
                          style={{
                            transform: "translate(30%, -30%)",
                            borderRadius: "50%",
                            padding: "0px 6px",
                          }}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddGallery;
